import {
  DEFAULT_ARCGIS_ONLINE_PORTAL,
  buildRedirectUri,
  getConfiguredBasePath,
  getRuntimeAuthConfig,
  normalizePortalUrl,
  requireClientId,
} from './auth/config.js';
import {
  beginArcgisSignIn,
  completeArcgisSignIn,
  hasOAuthResponse,
  isOAuthCallbackRoute,
  restoreActiveArcgisAccount,
  signOutArcgisAccount,
} from './auth/identity.js';
import { ARCGIS_ACCOUNT_STORAGE_KEY, ARCGIS_AUTH_BROADCAST_CHANNEL, type StoredArcgisAccount } from './auth/storage.js';

declare global {
  interface Window {
    APP_BASE_PATH?: string;
    ARCGIS_AUTH_CONFIG?: {
      onlinePortalUrl?: string;
      onlineClientId?: string;
      authCallbackPath?: string;
      tokenExpirationMinutes?: number;
    };
    /**
     * Test/development-only flag. The server only substitutes `true` here
     * when NODE_ENV is `development` or `test` AND `ALLOW_AUTH_BYPASS=1`
     * is set AND the request hostname is a localhost address (see
     * `isAuthBypassEnabled` + `isLocalhostHostname` in src/web/server.ts).
     * In production builds and in the R2 static deliverable this is hard-
     * coded to `false`, so the sign-in gate cannot be skipped. The
     * browser additionally re-checks `location.hostname` before honoring
     * the flag as defense-in-depth.
     */
    E2E_AUTH_BYPASS?: boolean;
    /** True only in the disconnected static artifact. */
    OFFLINE_MODE?: boolean;
  }
}

interface CalciteButtonElement extends HTMLElement {
  disabled?: boolean;
  hidden: boolean;
  loading?: boolean;
}

interface CalciteDropdownElement extends HTMLElement {
  open?: boolean;
}

interface CalciteInputElement extends HTMLElement {
  value: string;
}

interface CalciteDialogElement extends HTMLElement {
  open: boolean;
}

interface AuthUiElements {
  onlineButton: CalciteButtonElement | null;
  gateOnlineButton: CalciteButtonElement | null;
  enterpriseButton: CalciteButtonElement | null;
  gateEnterpriseButton: CalciteButtonElement | null;
  signInDropdown: CalciteDropdownElement | null;
  signInMenuButton: CalciteButtonElement | null;
  userMenu: CalciteDropdownElement | null;
  userMenuAvatar: HTMLElement | null;
  userMenuFullName: HTMLElement | null;
  userMenuUsername: HTMLElement | null;
  userMenuSignOutItem: HTMLElement | null;
  statusText: HTMLElement | null;
  gate: HTMLElement | null;
  gateNotice: HTMLElement | null;
  skipLink: HTMLAnchorElement | null;
  enterpriseModal: CalciteDialogElement | null;
  enterprisePortalInput: CalciteInputElement | null;
  enterpriseClientIdInput: CalciteInputElement | null;
  enterpriseRedirectUriInput: CalciteInputElement | null;
  enterpriseContinueButton: CalciteButtonElement | null;
  enterpriseCancelButton: CalciteButtonElement | null;
  enterpriseNotice: HTMLElement | null;
}

let activeAccount: StoredArcgisAccount | undefined;

document.addEventListener('DOMContentLoaded', () => {
  initializeAuthUi()
    .catch((error) => {
      updateStatus(getAuthErrorMessage(error));
    })
    .finally(() => {
      // Reveal the app chrome and gate once we have determined the
      // sign-in state (see the `auth-pending` rule in public/index.html).
      // Use `finally` so an unexpected init error does not leave the
      // page in a permanently-blank state.
      document.documentElement.classList.remove('auth-pending');
    });
});

async function initializeAuthUi(): Promise<void> {
  const elements = getAuthUiElements();
  const runtimeConfig = getRuntimeAuthConfig(window);
  const basePath = getConfiguredBasePath(window);

  if (window.OFFLINE_MODE === true) {
    activeAccount = createOfflineAccount();
    renderAuthState(elements);
    return;
  }

  updateEnterpriseRedirectUri(elements, basePath, runtimeConfig.authCallbackPath);
  renderAuthState(elements);

  wireSignInControls(elements, runtimeConfig, basePath);
  wireAuthCompletionListeners(elements);

  // Development/test bypass. The server only substitutes `true` here
  // when NODE_ENV is in {development,test}, ALLOW_AUTH_BYPASS=1, AND
  // the request hostname is a localhost address (see isAuthBypassEnabled
  // + isLocalhostHostname in src/web/server.ts). Production builds and
  // the R2 static deliverable hard-code this to false. We additionally
  // re-check `location.hostname` here as a defense-in-depth guarantee
  // so the bypass cannot activate from a non-localhost origin even if
  // the server-rendered HTML somehow leaked the truthy flag.
  if (window.E2E_AUTH_BYPASS === true && isLocalhostBypassAllowed()) {
    activeAccount = createBypassAccount();
    renderAuthState(elements);
    return;
  }

  if (hasOAuthResponse(window.location)) {
    const completingCallbackRoute = isOAuthCallbackRoute(window.location, runtimeConfig.authCallbackPath);
    updateGateNotice(elements, 'Completing ArcGIS sign-in...');
    let completedAccount: StoredArcgisAccount | undefined;
    let completionError: unknown;
    try {
      completedAccount = await completeArcgisSignIn();
      if (completedAccount) {
        activeAccount = completedAccount;
        notifyAuthCompletion();
      } else {
        // No error thrown but no account either. The most common cause
        // is that `completeArcgisSignIn` couldn't find the pending
        // OAuth request in localStorage (e.g. the user cleared site
        // data between begin and complete, or the redirect landed in a
        // different storage partition / private window). Log it so we
        // can tell this case apart from a thrown library error.
        console.warn('[oauth] completeArcgisSignIn returned undefined (no pending request found in localStorage?)');
      }
    } catch (error) {
      completionError = error;
      if (!completingCallbackRoute) {
        throw error;
      }
    } finally {
      if (completingCallbackRoute) {
        clearCallbackUrl(basePath);
      }
    }
    if (completingCallbackRoute) {
      // Same-window OAuth flow: navigate back to the app home so the
      // wizard (or the auth gate, on failure) renders normally. We use
      // `location.replace` so the callback URL is removed from history.
      const homeUrl = basePath ? `${basePath}/` : '/';
      if (completedAccount && !completionError) {
        window.location.replace(homeUrl);
      } else {
        // Log to console so QA / support can see the underlying error
        // (the lib's `completeOAuth2` rejects with a specific reason
        // when PKCE state/verifier mismatch, network failure, or
        // expired auth code occurs). Without this the user just sees a
        // generic "not completed" message and the only clue is buried
        // in `completionError` which we previously discarded.
        if (completionError) console.error('[oauth] completeArcgisSignIn failed', completionError);
        const detail = completionError ? `: ${getAuthErrorMessage(completionError)}` : '';
        showOAuthCallbackStatus(`ArcGIS sign-in was not completed${detail}`, homeUrl);
      }
      return;
    }
    if (completedAccount) {
      clearCallbackUrl(basePath);
    }
    renderAuthState(elements);
    return;
  }

  updateGateNotice(elements, 'Checking ArcGIS sign-in...');
  activeAccount = await restoreActiveArcgisAccount();
  renderAuthState(elements);
}

function wireAuthCompletionListeners(elements: AuthUiElements): void {
  window.addEventListener('storage', (event) => {
    if (event.key !== ARCGIS_ACCOUNT_STORAGE_KEY) return;
    void restoreAuthStateFromStorage(elements);
  });

  if (!('BroadcastChannel' in window)) return;
  const channel = new BroadcastChannel(ARCGIS_AUTH_BROADCAST_CHANNEL);
  channel.addEventListener('message', (event) => {
    if ((event.data as { type?: string } | undefined)?.type !== 'arcgis-auth-complete') return;
    void restoreAuthStateFromStorage(elements);
  });
}

async function restoreAuthStateFromStorage(elements: AuthUiElements): Promise<void> {
  const restoredAccount = await restoreActiveArcgisAccount();
  if (!restoredAccount) return;
  activeAccount = restoredAccount;
  renderAuthState(elements);
}

/**
 * Builds a synthetic active account used only when the dev/test auth
 * bypass is enabled (see initializeAuthUi). The serializedSession is a
 * placeholder; this account is never written back to storage and is not
 * used to make ArcGIS API calls.
 */
function createBypassAccount(): StoredArcgisAccount {
  return {
    id: 'e2e-bypass:local:e2e-user',
    provider: 'arcgis-enterprise',
    portalUrl: 'https://localhost/portal',
    clientId: 'e2e-bypass',
    username: 'e2e-user',
    fullName: 'E2E Bypass User',
    orgName: 'Local E2E',
    serializedSession: '{}',
    updatedAt: new Date(0).toISOString(),
  };
}

function createOfflineAccount(): StoredArcgisAccount {
  return {
    id: 'offline:operator',
    provider: 'arcgis-enterprise',
    portalUrl: '',
    clientId: '',
    username: 'Offline operator',
    fullName: 'Offline mode',
    orgName: '',
    serializedSession: '{}',
    updatedAt: new Date(0).toISOString(),
  };
}

/**
 * Returns true only when the page is being served from a localhost
 * origin. The dev/test auth bypass requires this to honor the
 * server-supplied `E2E_AUTH_BYPASS` flag; remote origins are rejected
 * even if the flag is truthy in window. This matches the localhost
 * gate in src/web/server.ts (`isLocalhostHostname`).
 */
function isLocalhostBypassAllowed(): boolean {
  const hostname = window.location.hostname.toLowerCase();
  // window.location.hostname strips the port and (for IPv6) strips the
  // brackets, so `http://[::1]:3000` becomes `::1`. Match the same set
  // the server-side `isLocalhostHostname` helper accepts.
  return hostname === 'localhost' || hostname === '127.0.0.1' || hostname === '::1';
}

function notifyAuthCompletion(): void {
  if (!('BroadcastChannel' in window)) return;
  const channel = new BroadcastChannel(ARCGIS_AUTH_BROADCAST_CHANNEL);
  channel.postMessage({ type: 'arcgis-auth-complete' });
  channel.close();
}

function wireSignInControls(elements: AuthUiElements, runtimeConfig: ReturnType<typeof getRuntimeAuthConfig>, basePath: string): void {
  const startOnlineSignIn = async (clientId: string): Promise<void> => {
    activeAccount = await beginArcgisSignIn({
      provider: 'arcgis-online',
      portalUrl: runtimeConfig.onlinePortalUrl || DEFAULT_ARCGIS_ONLINE_PORTAL,
      clientId: requireClientId(clientId),
      basePath,
      callbackPath: runtimeConfig.authCallbackPath,
      expirationMinutes: runtimeConfig.tokenExpirationMinutes,
    });
    if (!activeAccount) {
      updateGateNotice(elements, 'ArcGIS sign-in was not completed.');
    }
    renderAuthState(elements);
  };

  // ArcGIS Online sign-in launches the OAuth popup directly against the
  // tool's registered `analytics4IoT` application (or the value of
  // ARCGIS_ONLINE_CLIENT_ID when the deployment overrides it). There is
  // no intermediate modal because the tool ships its own registered app
  // and end users do not need to supply their own client ID, mirroring
  // the ArcGIS Assistant sign-in flow.
  const openOnlineSignIn = async (trigger: CalciteButtonElement | null): Promise<void> => {
    closeSignInDropdown(elements);
    updateGateNotice(elements, '');
    await runWithLoading(trigger, async () => {
      try {
        await startOnlineSignIn(runtimeConfig.onlineClientId);
      } catch (error) {
        updateGateNotice(elements, getAuthErrorMessage(error));
      }
    });
  };

  const openEnterpriseSignIn = (): void => {
    closeSignInDropdown(elements);
    clearEnterpriseNotice(elements);
    updateGateNotice(elements, '');
    updateEnterpriseRedirectUri(elements, basePath, runtimeConfig.authCallbackPath);
    if (elements.enterpriseModal) elements.enterpriseModal.open = true;
  };

  elements.onlineButton?.addEventListener('click', () => {
    void openOnlineSignIn(elements.onlineButton);
  });
  elements.gateOnlineButton?.addEventListener('click', () => {
    void openOnlineSignIn(elements.gateOnlineButton);
  });

  elements.enterpriseButton?.addEventListener('click', openEnterpriseSignIn);
  elements.gateEnterpriseButton?.addEventListener('click', openEnterpriseSignIn);

  elements.enterpriseCancelButton?.addEventListener('click', () => {
    if (elements.enterpriseModal) elements.enterpriseModal.open = false;
  });

  elements.enterpriseContinueButton?.addEventListener('click', async () => {
    clearEnterpriseNotice(elements);
    await runWithLoading(elements.enterpriseContinueButton, async () => {
      try {
        const portalUrl = normalizePortalUrl(elements.enterprisePortalInput?.value ?? '');
        activeAccount = await beginArcgisSignIn({
          provider: 'arcgis-enterprise',
          portalUrl,
          clientId: elements.enterpriseClientIdInput?.value ?? '',
          basePath,
          callbackPath: runtimeConfig.authCallbackPath,
          expirationMinutes: runtimeConfig.tokenExpirationMinutes,
        });
        if (!activeAccount) {
          updateGateNotice(elements, 'ArcGIS sign-in was not completed.');
        }
        if (elements.enterpriseModal) elements.enterpriseModal.open = false;
        renderAuthState(elements);
      } catch (error) {
        showEnterpriseNotice(elements, getAuthErrorMessage(error));
      }
    });
  });

  elements.userMenuSignOutItem?.addEventListener('click', async () => {
    if (!activeAccount) return;
    try {
      await signOutArcgisAccount(activeAccount);
    } finally {
      activeAccount = undefined;
      window.location.assign(basePath || '/');
    }
  });
}

function getAuthUiElements(): AuthUiElements {
  return {
    onlineButton: getElement('signInOnlineButton'),
    gateOnlineButton: getElement('gateSignInOnlineButton'),
    enterpriseButton: getElement('signInEnterpriseButton'),
    gateEnterpriseButton: getElement('gateSignInEnterpriseButton'),
    signInDropdown: getElement('signInDropdown'),
    signInMenuButton: getElement('signInMenuButton'),
    userMenu: getElement('userMenu'),
    userMenuAvatar: getElement('userMenuAvatar'),
    userMenuFullName: getElement('userMenuFullName'),
    userMenuUsername: getElement('userMenuUsername'),
    userMenuSignOutItem: getElement('userMenuSignOutItem'),
    statusText: getElement('authStatusText'),
    gate: getElement('authGate'),
    gateNotice: getElement('authGateNotice'),
    skipLink: getElement('skipLink'),
    enterpriseModal: getElement('enterpriseAuthModal'),
    enterprisePortalInput: getElement('enterprisePortalInput'),
    enterpriseClientIdInput: getElement('enterpriseClientIdInput'),
    enterpriseRedirectUriInput: getElement('enterpriseRedirectUriInput'),
    enterpriseContinueButton: getElement('enterpriseContinueButton'),
    enterpriseCancelButton: getElement('enterpriseCancelButton'),
    enterpriseNotice: getElement('enterpriseAuthNotice'),
  };
}

function renderAuthState(elements: AuthUiElements): void {
  if (activeAccount) {
    const displayName = activeAccount.fullName?.trim() || activeAccount.username;
    updateStatus(`${activeAccount.username}${activeAccount.orgName ? ` - ${activeAccount.orgName}` : ''}`);
    if (elements.userMenuFullName) elements.userMenuFullName.textContent = displayName;
    if (elements.userMenuUsername) elements.userMenuUsername.textContent = activeAccount.username;
    if (elements.userMenuAvatar) {
      elements.userMenuAvatar.setAttribute('full-name', displayName);
      elements.userMenuAvatar.setAttribute('username', activeAccount.username);
      if (activeAccount.thumbnailDataUrl) {
        elements.userMenuAvatar.setAttribute('thumbnail', activeAccount.thumbnailDataUrl);
      } else {
        elements.userMenuAvatar.removeAttribute('thumbnail');
      }
    }
    document.body.classList.remove('auth-required');
    document.body.classList.add('authenticated');
    if (elements.skipLink) {
      elements.skipLink.href = '#mainContent';
      elements.skipLink.textContent = 'Skip to main content';
    }
    updateGateNotice(elements, '');
    setElementHidden(elements.statusText, true);
    setElementHidden(elements.signInDropdown, true);
    setElementHidden(elements.gateOnlineButton, true);
    setElementHidden(elements.gateEnterpriseButton, true);
    setElementHidden(elements.userMenu, false);
    return;
  }

  updateStatus('Sign in required');
  document.body.classList.add('auth-required');
  document.body.classList.remove('authenticated');
  if (elements.skipLink) {
    elements.skipLink.href = '#authGate';
    elements.skipLink.textContent = 'Skip to sign in';
  }
  updateGateNotice(elements, '');
  setElementHidden(elements.statusText, true);
  setElementHidden(elements.signInDropdown, false);
  setElementHidden(elements.gateOnlineButton, false);
  setElementHidden(elements.gateEnterpriseButton, false);
  setElementHidden(elements.userMenu, true);
}

function closeSignInDropdown(elements: AuthUiElements): void {
  if (elements.signInDropdown) elements.signInDropdown.open = false;
}

function updateStatus(message: string): void {
  const statusText = document.getElementById('authStatusText');
  if (statusText) statusText.textContent = message;
}

async function runWithLoading(button: CalciteButtonElement | null, run: () => Promise<void>): Promise<void> {
  try {
    if (button) {
      button.loading = true;
      button.disabled = true;
    }
    await run();
  } catch (error) {
    updateStatus(getAuthErrorMessage(error));
  } finally {
    if (button) {
      button.loading = false;
      button.disabled = false;
    }
  }
}

function showEnterpriseNotice(elements: AuthUiElements, message: string): void {
  if (!elements.enterpriseNotice) return;
  elements.enterpriseNotice.textContent = message;
  elements.enterpriseNotice.hidden = false;
  updateGateNotice(elements, message);
}

function clearEnterpriseNotice(elements: AuthUiElements): void {
  if (!elements.enterpriseNotice) return;
  elements.enterpriseNotice.textContent = '';
  elements.enterpriseNotice.hidden = true;
}

function updateEnterpriseRedirectUri(elements: AuthUiElements, basePath: string, callbackPath: string): void {
  if (!elements.enterpriseRedirectUriInput) return;
  elements.enterpriseRedirectUriInput.value = buildRedirectUri(window.location.origin, basePath, callbackPath);
}

function updateGateNotice(elements: AuthUiElements, message: string): void {
  if (!elements.gateNotice) return;
  elements.gateNotice.textContent = message;
  elements.gateNotice.hidden = !message;
}

function clearCallbackUrl(basePath: string): void {
  if (!window.location.pathname.endsWith('/auth/callback')) return;
  window.history.replaceState(null, document.title, basePath ? `${basePath}/` : '/');
}

function showOAuthCallbackStatus(message: string, homeUrl: string): void {
  document.body.classList.remove('auth-required', 'authenticated');
  const main = document.createElement('main');
  // Class `oauth-callback-status` is defined in public/index.html; using a
  // class instead of inline `style.*` mutations keeps this code path
  // compliant with the locked-down `style-src` / `style-src-attr` CSP.
  main.className = 'oauth-callback-status';
  main.setAttribute('role', 'status');
  const text = document.createElement('p');
  text.textContent = message;
  const link = document.createElement('a');
  link.href = homeUrl;
  link.textContent = 'Return to the migration tool';
  main.append(text, link);
  document.body.replaceChildren(main);
}

function setElementHidden(element: HTMLElement | null, hidden: boolean): void {
  if (element) element.hidden = hidden;
}

function getElement<T extends HTMLElement>(id: string): T | null {
  return document.getElementById(id) as T | null;
}

function getAuthErrorMessage(error: unknown): string {
  if (error instanceof Error) return error.message;
  return 'ArcGIS sign-in did not complete.';
}
